// src/components/CampaignSendingOverlay.js
import React, { useState, useEffect, useRef } from 'react';

export default function CampaignSendingOverlay({ onComplete, patientCount = 30 }) {
  const [isVisible, setIsVisible] = useState(false);
  const [patients, setPatients] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [sentCount, setSentCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Reference for the list container to enable autoscroll
  const listBodyRef = useRef(null);
  // Reference to track which patient is currently being processed
  const [activeIndex, setActiveIndex] = useState(-1);

  // Generate patient data
  useEffect(() => {
    const names = [
      'Sarah Mitchell', 'James Wong', 'Emma Thompson', 'Michael Chen', 
      'Jessica Davis', 'David Kim', 'Rachel Patel', 'Daniel Tan', 
      'Laura Ng', 'Kevin Low', 'Amanda Lee', 'Christopher Goh',
      'Michelle Ho', 'Jason Lim', 'Stephanie Tan', 'Ryan Ong',
      'Nicole Chua', 'Brandon Yeo', 'Tiffany Koh', 'Marcus Ng',
      'Olivia Tay', 'Nathaniel Chan', 'Isabella Lim', 'Lucas Teo',
      'Sophia Yap', 'Ethan Phua', 'Chloe Wee', 'Mason Chong',
      'Grace Lim', 'Oliver Sim'
    ];
    
    // Generate full patient list (use names cyclically with variations)
    const patientList = [];
    for (let i = 0; i < patientCount; i++) {
      const baseName = names[i % names.length];
      const variation = Math.floor(i / names.length);
      const name = variation === 0 ? baseName : `${baseName} ${variation + 1}`;
      patientList.push({
        id: i + 1,
        name: name,
        status: 'pending' // pending | preparing | sending | sent
      });
    }
    setPatients(patientList);
  }, [patientCount]);

  // Fade-in animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Autoscroll function
  const scrollToActivePatient = () => {
    if (listBodyRef.current && activeIndex >= 0) {
      const listElement = listBodyRef.current;
      // Find the active patient element
      const items = listElement.querySelectorAll('[data-patient-id]');
      if (items[activeIndex]) {
        const activeItem = items[activeIndex];
        // Scroll to make the active item visible
        activeItem.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  // Trigger autoscroll when activeIndex changes
  useEffect(() => {
    if (activeIndex >= 0) {
      // Small delay to ensure DOM has updated
      const scrollTimer = setTimeout(scrollToActivePatient, 100);
      return () => clearTimeout(scrollTimer);
    }
  }, [activeIndex]);

  // Orchestrate the sending animation
  useEffect(() => {
    if (!isVisible || patients.length === 0) return;

    let index = 0;
    const batchSize = 3; // Number of patients to process simultaneously
    let completed = 0;
    let currentBatchStart = 0;

    const processNextBatch = () => {
      if (completed >= patients.length) {
        setIsComplete(true);
        // Show completion for 2.5 seconds then transition
        setTimeout(() => {
          if (onComplete) onComplete();
        }, 2500);
        return;
      }

      // Calculate how many to process in this batch
      const remaining = patients.length - completed;
      const batchCount = Math.min(batchSize, remaining);
      
      // Update batch to "preparing" state
      const startIdx = completed;
      const endIdx = startIdx + batchCount;
      currentBatchStart = startIdx;
      
      // Set active index to the first patient in this batch
      setActiveIndex(startIdx);
      
      setPatients(prev => 
        prev.map((p, idx) => {
          if (idx >= startIdx && idx < endIdx && p.status === 'pending') {
            return { ...p, status: 'preparing' };
          }
          return p;
        })
      );

      // After brief preparing, move to sending
      setTimeout(() => {
        setPatients(prev => 
          prev.map((p, idx) => {
            if (idx >= startIdx && idx < endIdx && p.status === 'preparing') {
              return { ...p, status: 'sending' };
            }
            return p;
          })
        );

        // After sending, mark as sent
        setTimeout(() => {
          setPatients(prev => {
            const updated = prev.map((p, idx) => {
              if (idx >= startIdx && idx < endIdx && p.status === 'sending') {
                return { ...p, status: 'sent' };
              }
              return p;
            });
            
            // Count sent patients
            const sent = updated.filter(p => p.status === 'sent').length;
            setSentCount(sent);
            setProgress((sent / patients.length) * 100);
            
            return updated;
          });

          completed += batchCount;
          
          // Update active index for next batch
          if (completed < patients.length) {
            setActiveIndex(completed);
          }
          
          // Continue with next batch after a short pause
          setTimeout(processNextBatch, 300);
        }, 400);
      }, 350);
    };

    // Start the process after a brief initial delay
    const startTimer = setTimeout(processNextBatch, 600);
    return () => clearTimeout(startTimer);
  }, [isVisible, patients, onComplete]);

  // Get displayed patients (show all, with scroll)
  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending':
        return <span style={campaignStyles.statusDotPending} />;
      case 'preparing':
        return <span style={campaignStyles.statusDotPreparing} />;
      case 'sending':
        return <span style={campaignStyles.statusDotSending} />;
      case 'sent':
        return <span style={campaignStyles.statusDotSent}>✓</span>;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending':
        return <span style={campaignStyles.statusTextPending}>Waiting</span>;
      case 'preparing':
        return <span style={campaignStyles.statusTextPreparing}>Preparing...</span>;
      case 'sending':
        return <span style={campaignStyles.statusTextSending}>Sending...</span>;
      case 'sent':
        return <span style={campaignStyles.statusTextSent}>✓ Sent</span>;
      default:
        return null;
    }
  };

  // Calculate stats
  const totalPatients = patients.length;
  const remaining = totalPatients - sentCount;

  return (
    <div style={campaignStyles.overlay}>
      <div style={campaignStyles.glowBg} />
      
      <div 
        style={{
          ...campaignStyles.content,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        {/* Header */}
        <div style={campaignStyles.header}>
          <div style={campaignStyles.headerLeft}>
            <span style={campaignStyles.headerIcon}></span>
            <span style={campaignStyles.headerText}>Sending Personalized Recalls on your Behalf</span>
          </div>
          <div style={campaignStyles.headerRight}>
            <span style={campaignStyles.counter}>
              {sentCount} / {totalPatients}
            </span>
            <span style={campaignStyles.counterLabel}>Messages Sent</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={campaignStyles.progressContainer}>
          <div style={campaignStyles.progressTrack}>
            <div 
              style={{
                ...campaignStyles.progressBar,
                width: `${progress}%`,
                background: progress === 100 
                  ? 'linear-gradient(90deg, #2D9B8A, #3ABFA8)'
                  : 'linear-gradient(90deg, #2D9B8A, #3ABFA8)',
              }}
            />
          </div>
          <div style={campaignStyles.progressStats}>
            <span style={campaignStyles.progressLabel}>
              {isComplete ? '✓ Complete!' : `${Math.round(progress)}% Complete`}
            </span>
            <span style={campaignStyles.progressSubtext}>
              {isComplete 
                ? 'All messages sent successfully' 
                : `${remaining} remaining`}
            </span>
          </div>
        </div>

        {/* Patient List */}
        <div style={campaignStyles.listContainer}>
          <div style={campaignStyles.listHeader}>
            <span style={campaignStyles.listHeaderPatient}>Patient</span>
            <span style={campaignStyles.listHeaderStatus}>Status</span>
          </div>
          
          <div 
            ref={listBodyRef}
            style={campaignStyles.listBody}
          >
            {patients.slice(0, 30).map((patient, index) => (
              <div 
                key={patient.id}
                data-patient-id={patient.id}
                style={{
                  ...campaignStyles.listItem,
                  animation: patient.status !== 'pending' 
                    ? 'slideIn 0.3s ease-out' 
                    : 'none',
                  opacity: patient.status === 'pending' ? 0.4 : 1,
                  background: index === activeIndex && patient.status !== 'sent'
                    ? 'rgba(45, 155, 138, 0.08)'
                    : 'transparent',
                  borderLeft: index === activeIndex && patient.status !== 'sent'
                    ? '2px solid #3ABFA8'
                    : '2px solid transparent',
                  transition: 'all 0.3s ease',
                }}
              >
                <span style={campaignStyles.patientName}>
                  {patient.name}
                </span>
                <div style={campaignStyles.statusContainer}>
                  {getStatusIcon(patient.status)}
                  {getStatusText(patient.status)}
                </div>
              </div>
            ))}
            
            {patients.length > 30 && (
              <div style={campaignStyles.moreIndicator}>
                + {patients.length - 30} more patients processing...
              </div>
            )}
          </div>
        </div>

        {/* Completion Badge */}
        {isComplete && (
          <div style={campaignStyles.completionBadge}>
            <span style={campaignStyles.completionIcon}>🎯</span>
            <span style={campaignStyles.completionText}>
              Campaign Complete!
            </span>
            <span style={campaignStyles.completionSubtext}>
              {totalPatients} messages sent
            </span>
          </div>
        )}

        {/* Footer */}
        <div style={campaignStyles.footer}>
          <span style={campaignStyles.footerText}>
            {!isComplete 
              ? 'Automatically sending personalized messages...' 
              : '✓ All messages delivered successfully'}
          </span>
          {!isComplete && (
            <span style={campaignStyles.footerSpeed}>
              ~{Math.round((sentCount / (totalPatients || 1)) * 100)}% complete
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const campaignStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: '#070E16',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: 24,
    overflow: 'hidden',
  },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 800,
    height: '70%',
    maxHeight: 700,
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.08) 0%, rgba(45, 155, 138, 0.02) 40%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
    maxWidth: 700,
    width: '100%',
    gap: 16,
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 4px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerIcon: {
    fontSize: 20,
  },
  headerText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  counter: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.5px',
  },
  counterLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.3px',
  },

  // Progress
  progressContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  progressTrack: {
    width: '100%',
    height: 4,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  progressStats: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressLabel: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontWeight: 500,
  },
  progressSubtext: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 12,
  },

  // Patient List
  listContainer: {
    width: '100%',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    overflow: 'hidden',
    maxHeight: 400,
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
  },
  listHeaderPatient: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  listHeaderStatus: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  listBody: {
    padding: '4px 0',
    maxHeight: 340,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.1) transparent',
    position: 'relative',
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 20px',
    transition: 'all 0.3s ease',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
    paddingLeft: 22, // Adjust for the border
  },
  patientName: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    fontWeight: 500,
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  
  // Status Dots
  statusDotPending: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
  },
  statusDotPreparing: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#F59E0B',
    animation: 'pulse 0.8s ease-in-out infinite',
  },
  statusDotSending: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#3B82F6',
    animation: 'pulse 0.5s ease-in-out infinite',
  },
  statusDotSent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: 'rgba(45, 155, 138, 0.2)',
    color: '#3ABFA8',
    fontSize: 12,
    fontWeight: 700,
  },
  
  // Status Text
  statusTextPending: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 12,
    fontWeight: 400,
  },
  statusTextPreparing: {
    color: '#F59E0B',
    fontSize: 12,
    fontWeight: 500,
  },
  statusTextSending: {
    color: '#3B82F6',
    fontSize: 12,
    fontWeight: 500,
  },
  statusTextSent: {
    color: '#3ABFA8',
    fontSize: 12,
    fontWeight: 600,
  },

  moreIndicator: {
    padding: '12px 20px',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.3)',
    fontSize: 13,
    fontStyle: 'italic',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },

  // Completion Badge
  completionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'rgba(45, 155, 138, 0.15)',
    border: '1px solid rgba(45, 155, 138, 0.3)',
    borderRadius: 30,
    padding: '10px 24px',
    animation: 'fadeIn 0.6s ease-out',
  },
  completionIcon: {
    fontSize: 20,
  },
  completionText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 600,
  },
  completionSubtext: {
    color: '#3ABFA8',
    fontSize: 13,
    fontWeight: 500,
    paddingLeft: 12,
    borderLeft: '1px solid rgba(45, 155, 138, 0.3)',
  },

  // Footer
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 4px',
  },
  footerText: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 13,
    letterSpacing: '0.3px',
  },
  footerSpeed: {
    color: 'rgba(255,255,255,0.2)',
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
  },
};

// Add keyframes
const campaignStyleSheet = document.createElement("style");
campaignStyleSheet.textContent = `
  @keyframes slideIn {
    0% { opacity: 0; transform: translateX(-10px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(10px) scale(0.95); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
`;
document.head.appendChild(campaignStyleSheet);